import { makeEntityId } from '@web-platform/shared-universal-types-utils/src/utils';
import { nonNullishOrThrow } from '@web-platform/shared-universal-utils-frontend/src/helpers/assertNonNullish';
import { sonarLocalDateTimeToISOString } from '@web-platform/shared-universal-utils-frontend/src/helpers/grpcLocalDateTimeToISOString';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import type { SkyscannerSonarV3Response } from '@skyscanner-internal/sonar-web-http-client';
import type { LegBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/legsBookingPanel';
import type { EntityId } from '@web-platform/shared-universal-types-utils/src/utils';

type SonarLeg = SkyscannerSonarV3Response.Leg;
type Layover = SkyscannerSonarV3Response.Layover;
type Segment = SkyscannerSonarV3Response.Segment;

const getUniqueCarrierIds = (
  carrierType: 'marketingCarrier' | 'operatingCarrier',
  segments: Segment[],
) =>
  Array.from(
    new Set(
      segments.map((segment) =>
        nonNullishOrThrow(segment?.[carrierType]?.id, `leg.segment.${carrierType}.id`),
      ),
    ),
  );

const getStopIds = (layovers: Layover[] | undefined): string[][] | undefined =>
  layovers?.map(({ destination, origin }) => {
    const originAltId = nonNullishOrThrow(origin?.airport?.altId, 'origin.airport.altId');
    const destAltId = nonNullishOrThrow(destination?.airport?.altId, 'destination.airport.altId');
    return originAltId === destAltId ? [originAltId] : [originAltId, destAltId];
  });

const getStopEntityIds = (layovers: Layover[] | undefined): EntityId[][] | undefined =>
  layovers?.map(({ destination, origin }) => {
    const originEntityId = makeEntityId(
      nonNullishOrThrow(origin?.airport?.entityId, 'origin.airport.entityId'),
    );
    const destEntityId = makeEntityId(
      nonNullishOrThrow(destination?.airport?.entityId, 'destination.airport.entityId'),
    );
    return originEntityId === destEntityId ? [originEntityId] : [originEntityId, destEntityId];
  });

export const mapSonarV3Legs = (legs?: SonarLeg[]): LegBookingPanel[] => {
  try {
    return (
      nonNullishOrThrow(legs, 'legs').map((leg) => {
        const segments = nonNullishOrThrow(leg.segments, 'leg.segments');
        const stopIds = getStopIds(leg.layovers);
        const stopEntityIds = getStopEntityIds(leg.layovers);

        return {
          id: nonNullishOrThrow(leg.id, 'leg.id'),
          origin_place_id: nonNullishOrThrow(
            leg.origin?.airport?.altId,
            'leg.origin.airport.altId',
          ),
          origin_entity_id: makeEntityId(
            nonNullishOrThrow(leg.origin?.airport?.entityId, 'leg.origin.airport.entityId'),
          ),
          destination_place_id: nonNullishOrThrow(
            leg.destination?.airport?.altId,
            'leg.destination.airport.altId',
          ),
          destination_entity_id: makeEntityId(
            nonNullishOrThrow(
              leg.destination?.airport?.entityId,
              'leg.destination.airport.entityId',
            ),
          ),
          departure: sonarLocalDateTimeToISOString(
            nonNullishOrThrow(leg.departure, 'leg.departure'),
          ),
          arrival: sonarLocalDateTimeToISOString(nonNullishOrThrow(leg.arrival, 'leg.arrival')),
          segment_ids: segments.map((segment) => nonNullishOrThrow(segment.id, 'leg.segment.id')),
          duration: nonNullishOrThrow(leg.durationMinutes, 'leg.durationMinutes'),
          stop_count: nonNullishOrThrow(leg.stopCount, 'leg.stopCount'),
          marketing_carrier_ids: getUniqueCarrierIds('marketingCarrier', segments),
          operating_carrier_ids: getUniqueCarrierIds('operatingCarrier', segments),
          ...(stopIds
            ? {
                stop_ids: stopIds,
              }
            : {}),
          ...(stopEntityIds
            ? {
                stop_entity_ids: stopEntityIds,
              }
            : {}),
        };
      }) ?? []
    );
  } catch (error) {
    logError({
      component: 'mapSonarV3Legs',
      message: `SONARV3: Error mapping legs`,
      data: JSON.stringify(legs),
      error,
      stack: error?.stack,
    });

    return [];
  }
};
